// The Reports screen's server state (spec/13 §2, §15; spec/17):
//
//   GET /v1/organizations/{o}/reports/range?preset[&venueId]           reports:read        a preset's business days (F-ANL-03)
//   GET /v1/organizations/{o}/reports/revenue?from&to[&venueId]        reports:financial   Overview, Revenue, Passes & wallet
//   GET /v1/organizations/{o}/reports/sessions?from&to[&venueId]       reports:read        Overview, Sessions & occupancy
//   GET /v1/organizations/{o}/reports/products?from&to[&venueId]       reports:financial   Products
//   GET /v1/organizations/{o}/reports/staff?from&to[&venueId]          reports:financial   Staff
//   GET /v1/organizations/{o}/venues/{v}/reports/overview?from&to      reports:financial   the day close's sessions (F-ANL-01)
//   GET /v1/organizations/{o}/venues/{v}/reports/games-played?from&to  reports:read        Games played (F-CAT-04)
//   GET  /v1/organizations/{o}/reports/daily-summary?day&venueId        owners only         Daily summary (F-ANL-07)
//   POST /v1/organizations/{o}/reports/daily-summary/send               owners only         email it to yourself
//
// Without `venueId` a report rolls up every venue the member works at. Every figure is the server's; a minute's
// staleness is fine for a report.

import type { ReportPreset } from '@clavian/api-client';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const reportKeys = {
  gamesPlayed: (organizationId: string, venueId: string, from: string, to: string) => ['reports', organizationId, venueId, 'games-played', from, to] as const,
  overview: (organizationId: string, venueId: string, from: string, to: string) => ['reports', organizationId, venueId, 'overview', from, to] as const,
  range: (organizationId: string, preset: string, venueId: string | undefined) => ['reports', organizationId, venueId ?? 'all', 'range', preset] as const,
  tab: (organizationId: string, tab: string, venueId: string | undefined, from: string, to: string) => ['reports', organizationId, venueId ?? 'all', tab, from, to] as const,
};

/** A report's scope: the organisation, one venue or (undefined) every venue the member works at, and its days. */
export interface ReportScope {
  readonly organizationId: string;
  readonly venueId: string | undefined;
  readonly from: string;
  readonly to: string;
}

const scopeQuery = (scope: ReportScope) => ({ from: scope.from, to: scope.to, ...(scope.venueId === undefined ? {} : { venueId: scope.venueId }) });

/** A preset's business days in the venue's timezone and cutoff, and the previous period. */
export function useReportRange(organizationId: string, preset: ReportPreset | undefined, venueId: string | undefined, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: reportKeys.range(organizationId, preset ?? '', venueId),
    queryFn: () => api.getReportRange({ path: { organizationId }, query: { preset: preset ?? 'last7', ...(venueId === undefined ? {} : { venueId }) } }),
    enabled: enabled && preset !== undefined,
    staleTime: 60_000,
  });
}

export function useRevenueReport(scope: ReportScope, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: reportKeys.tab(scope.organizationId, 'revenue', scope.venueId, scope.from, scope.to),
    queryFn: () => api.getRevenueReport({ path: { organizationId: scope.organizationId }, query: scopeQuery(scope) }),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useSessionsReport(scope: ReportScope, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: reportKeys.tab(scope.organizationId, 'sessions', scope.venueId, scope.from, scope.to),
    queryFn: () => api.getSessionsReport({ path: { organizationId: scope.organizationId }, query: scopeQuery(scope) }),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useProductsReport(scope: ReportScope, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: reportKeys.tab(scope.organizationId, 'products', scope.venueId, scope.from, scope.to),
    queryFn: () => api.getProductsReport({ path: { organizationId: scope.organizationId }, query: scopeQuery(scope) }),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useStaffReport(scope: ReportScope, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: reportKeys.tab(scope.organizationId, 'staff', scope.venueId, scope.from, scope.to),
    queryFn: () => api.getStaffReport({ path: { organizationId: scope.organizationId }, query: scopeQuery(scope) }),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** The venue's canonical metrics over business days `from` … `to` — the day close reads the day's sessions from it. */
export function useVenueReportOverview(organizationId: string, venueId: string, from: string, to: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: reportKeys.overview(organizationId, venueId, from, to),
    queryFn: () => api.getVenueReportOverview({ path: { organizationId, venueId }, query: { from, to } }),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * The owners' daily summary for one business day (N-27): the organisation's (no venue) or one venue's — the one that
 * went out by email when there is one. `day` absent: the latest day ended at every venue. An owner's alone.
 */
export function useDailySummary(organizationId: string, venueId: string | undefined, day: string | undefined, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', organizationId, venueId ?? 'all', 'daily-summary', day ?? 'latest'] as const,
    queryFn: () => api.getDailySummary({ path: { organizationId }, query: { ...(day === undefined ? {} : { day }), ...(venueId === undefined ? {} : { venueId }) } }),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** Email the daily summary to yourself now: your own verified address and your inbox, nobody else's. */
export function useSendDailySummary(organizationId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (body: { readonly day?: string; readonly venueId?: string }) => api.sendDailySummary({ path: { organizationId }, body }),
  });
}

/** The venue's most-played titles over business days `from` … `to`. Plays arrive as sessions end, so a minute's staleness is fine. */
export function useGamesPlayedReport(organizationId: string, venueId: string, from: string, to: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: reportKeys.gamesPlayed(organizationId, venueId, from, to),
    queryFn: () => api.getGamesPlayedReport({ path: { organizationId, venueId }, query: { from, to } }),
    enabled,
    staleTime: 60_000,
  });
}
